import React, { useState, useEffect } from 'react';
import { Key, Plus, Search, Trash2, Edit, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { adminAPI } from '../../services/api';
import './AdminAPIKeys.css';

interface APIKey {
  id: string;
  key?: string;
  api_key?: string;
  plan: string;
  status: string;
  owner_email?: string;
  email?: string;
  name?: string;
  created_at: string;
  expires_at?: string;
  usage?: {
    total_requests: number;
    total_tokens: number;
  };
  total_requests?: number;
  total_tokens?: number;
  limits?: {
    requests_per_minute: number;
    max_tokens_per_request: number;
    max_ips: number;
  };
  requests_per_minute?: number;
  max_tokens_per_request?: number;
  max_ips?: number;
}

const AdminAPIKeys: React.FC = () => {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    plan: 'free',
    owner_email: '',
    validity_days: 365
  });

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listAPIKeys();
      const rawKeys = response.data.keys || [];
      
      // Normalize backend response to frontend format
      const normalizedKeys = rawKeys.map((k: any) => ({
        id: k.id,
        key: k.api_key || k.key,
        plan: k.plan,
        status: k.status,
        owner_email: k.email || k.owner_email,
        created_at: k.created_at,
        expires_at: k.expires_at,
        usage: {
          total_requests: k.total_requests || k.usage?.total_requests || 0,
          total_tokens: k.total_tokens || k.usage?.total_tokens || 0
        },
        limits: {
          requests_per_minute: k.requests_per_minute || k.limits?.requests_per_minute || 0,
          max_tokens_per_request: k.max_tokens_per_request || k.limits?.max_tokens_per_request || 0,
          max_ips: k.max_ips || k.limits?.max_ips || 0
        }
      }));
      
      setKeys(normalizedKeys);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const payload = {
        name: createForm.owner_email.split('@')[0] || 'User',
        email: createForm.owner_email,
        plan: createForm.plan,
        expiry_days: createForm.validity_days
      };
      await adminAPI.createAPIKey(payload);
      setShowCreateModal(false);
      setCreateForm({ plan: 'free', owner_email: '', validity_days: 365 });
      fetchKeys();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create API key');
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      await adminAPI.deleteAPIKey(keyId);
      fetchKeys();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete API key');
    }
  };

  const filteredKeys = keys.filter(key =>
    (key.key && key.key.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (key.owner_email && key.owner_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (key.plan && key.plan.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getPlanColor = (plan: string) => {
    const colors: any = {
      free: 'secondary',
      pro: 'primary',
      enterprise: 'warning'
    };
    return colors[plan.toLowerCase()] || 'secondary';
  };

  return (
    <div className="admin-api-keys">
      <div className="page-header">
        <div>
          <h1>API Key Management</h1>
          <p>Create and manage all API keys</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          Create API Key
        </button>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search API keys by key, owner, or plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* API Keys Table */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Key size={20} />
            All API Keys ({filteredKeys.length})
          </h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading"></div>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>API Key</th>
                  <th>Owner</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Usage</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredKeys.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <div className="key-cell">
                        <Key size={16} />
                        <span className="monospace">
                          {key.key ? key.key.substring(0, 20) + '...' : 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-secondary">{key.owner_email || key.email || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={`badge badge-${getPlanColor(key.plan || 'free')}`}>
                        {(key.plan || 'FREE').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${key.status === 'active' ? 'success' : 'error'}`}>
                        {key.status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {key.status || 'unknown'}
                      </span>
                    </td>
                    <td>
                      <div className="usage-cell">
                        <div>{(key.usage?.total_requests || 0).toLocaleString()} reqs</div>
                        <small>{(key.usage?.total_tokens || 0).toLocaleString()} tokens</small>
                      </div>
                    </td>
                    <td>
                      {key.expires_at ? (
                        <div className="date-cell">
                          <Calendar size={16} />
                          {new Date(key.expires_at).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-secondary">Never</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline">
                          <Edit size={16} />
                        </button>
                        <button 
                          className="btn btn-sm btn-error"
                          onClick={() => handleDeleteKey(key.id)}
                          disabled={!key.key}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New API Key</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-control"
                  value={createForm.plan}
                  onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })}
                >
                  <option value="free">Free (10 req/min, 1 IP)</option>
                  <option value="pro">Pro (100 req/min, 3 IPs)</option>
                  <option value="enterprise">Enterprise (6000 req/min, 30 IPs)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Owner Email (optional)</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="user@example.com"
                  value={createForm.owner_email}
                  onChange={(e) => setCreateForm({ ...createForm, owner_email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Validity (days)</label>
                <input
                  type="number"
                  className="form-control"
                  value={createForm.validity_days}
                  onChange={(e) => setCreateForm({ ...createForm, validity_days: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateKey}>
                <Plus size={16} />
                Create Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAPIKeys;